import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface GlobalAccountant {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
}

/**
 * Hook pour récupérer les comptables "globaux" d'une agence.
 * Un comptable est considéré comme "global" s'il est associé à 100% des résidences de l'agence.
 *
 * @param agencyId - ID de l'agence
 * @returns Liste des comptables globaux
 */
export function useGlobalAccountants(agencyId?: string) {
  return useQuery({
    queryKey: ['global-accountants', agencyId],
    queryFn: async () => {
      if (!agencyId) return []

      // 1. Compter le nombre total de résidences de l'agence
      const { count: totalResidences, error: countError } = await supabase
        .from('residences')
        .select('*', { count: 'exact', head: true })
        .eq('agency_id', agencyId)

      if (countError) throw countError
      if (!totalResidences || totalResidences === 0) return []

      // 2. Récupérer tous les ACCOUNTANT de l'agence avec le nombre de résidences associées
      const { data: accountants, error: accountantsError } = await supabase
        .from('users')
        .select(`
          id,
          email,
          first_name,
          last_name,
          user_residences!inner (
            residence_id,
            role,
            residences!inner (
              agency_id
            )
          )
        `)
        .eq('agency_id', agencyId)
        .eq('user_residences.role', 'ACCOUNTANT')
        .eq('user_residences.residences.agency_id', agencyId)

      if (accountantsError) throw accountantsError
      if (!accountants || accountants.length === 0) return []

      // 3. Filtrer pour ne garder que ceux associés à TOUTES les résidences
      const globalAccountants: GlobalAccountant[] = []

      for (const accountant of accountants) {
        // Compter le nombre de résidences uniques pour ce comptable
        const residenceIds = new Set(
          accountant.user_residences.map((ur: any) => ur.residence_id)
        )

        // Si nombre de résidences du comptable === nombre total de résidences
        if (residenceIds.size === totalResidences) {
          globalAccountants.push({
            id: accountant.id,
            email: accountant.email,
            first_name: accountant.first_name,
            last_name: accountant.last_name,
          })
        }
      }

      return globalAccountants
    },
    enabled: !!agencyId,
  })
}
